import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FiAlertTriangle, FiCheckCircle, FiEdit2, FiImage, FiPlus, FiSearch, FiTag, FiTrash2, FiX } from 'react-icons/fi';
import BrandLogoImage from '../../components/BrandLogoImage';
import './AdminUI.css';

const API_BASE = 'https://valleyapi-dr9y.onrender.com';
const emptyForm = { brand_name: '', brand_description: '', brand_image: '' };

function BrandLogo({ brand, size = 'small' }) {
  return (
    <div className={`va-brand-logo va-brand-logo--${size}`}>
      <span>{String(brand.brand_name || 'BR').slice(0, 2).toUpperCase()}</span>
      <BrandLogoImage brand={brand} alt="" />
    </div>
  );
}

export default function AdminBrands() {
  const [brands, setBrands] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [editingBrand, setEditingBrand] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [brandImageFile, setBrandImageFile] = useState(null);

  const fetchBrands = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`${API_BASE}/api/brands`, { cache: 'no-store' });
      if (!response.ok) throw new Error(`Request failed with status ${response.status}`);
      const data = await response.json();
      setBrands(Array.isArray(data) ? data : []);
    } catch (fetchError) {
      console.error('Unable to load brands:', fetchError);
      setError('Brands could not be loaded. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchBrands(); }, [fetchBrands]);

  const filteredBrands = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) return brands;
    return brands.filter((brand) => `${brand.brand_name || ''} ${brand.brand_description || ''}`.toLowerCase().includes(query));
  }, [brands, searchTerm]);

  const openCreate = () => {
    setEditingBrand(null);
    setFormData(emptyForm);
    setBrandImageFile(null);
    setError('');
    setSuccess('');
    setFormOpen(true);
  };

  const openEdit = (brand) => {
    setEditingBrand(brand);
    setFormData({
      brand_name: brand.brand_name || '',
      brand_description: brand.brand_description || '',
      brand_image: brand.brand_image || '',
    });
    setBrandImageFile(null);
    setError('');
    setSuccess('');
    setFormOpen(true);
  };

  const closeForm = () => {
    if (saving) return;
    setFormOpen(false);
    setEditingBrand(null);
    setFormData(emptyForm);
    setBrandImageFile(null);
  };

  const handleNameChange = (event) => {
    const name = event.target.value;
    setFormData((current) => ({
      ...current,
      brand_name: name,
      brand_image: editingBrand || current.brand_image
        ? current.brand_image
        : name.trim().replace(/[\s.\-–—]+/g, '_').replace(/_+/g, '_').toUpperCase(),
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setSuccess('');
    setSaving(true);
    const isEditing = Boolean(editingBrand);
    const url = isEditing ? `${API_BASE}/api/brands/${editingBrand.idbrand}` : `${API_BASE}/api/brands`;
    const requestBody = new FormData();
    requestBody.append('brand_name', formData.brand_name.trim());
    requestBody.append('brand_description', formData.brand_description.trim());
    requestBody.append('brand_image', formData.brand_image.trim().replace(/\.(jpg|jpeg|png|webp)$/i, ''));
    if (brandImageFile) requestBody.append('brand_image_file', brandImageFile);

    try {
      const response = await fetch(url, {
        method: isEditing ? 'PUT' : 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('admin_token')}`,
        },
        body: requestBody,
      });
      const text = await response.text();
      let data = {};
      try { data = text ? JSON.parse(text) : {}; } catch { data = {}; }
      if (!response.ok) throw new Error(data.error || data.message || `Request failed with status ${response.status}`);

      await fetchBrands();
      setSuccess(isEditing ? 'Brand updated successfully.' : 'Brand added successfully.');
      setFormOpen(false);
      setEditingBrand(null);
      setFormData(emptyForm);
      setBrandImageFile(null);
    } catch (saveError) {
      console.error('Unable to save brand:', saveError);
      setError(saveError.message || 'The brand could not be saved.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (brand) => {
    if (!window.confirm(`Delete “${brand.brand_name}” from the brands catalog?`)) return;
    setError('');
    setSuccess('');
    try {
      const response = await fetch(`${API_BASE}/api/brands/${brand.idbrand}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` },
      });
      const text = await response.text();
      let data = {};
      try { data = text ? JSON.parse(text) : {}; } catch { data = {}; }
      if (!response.ok) throw new Error(data.error || data.message || `Request failed with status ${response.status}`);
      await fetchBrands();
      setSuccess('Brand removed from the catalog.');
    } catch (deleteError) {
      console.error('Unable to delete brand:', deleteError);
      setError(deleteError.message || 'The brand could not be deleted.');
    }
  };

  const withDescription = brands.filter((brand) => brand.brand_description?.trim()).length;
  const withImage = brands.filter((brand) => brand.brand_image?.trim()).length;

  return (
    <div className="va-page">
      <header className="va-page-header">
        <div>
          <span className="va-eyebrow">Catalog management</span>
          <h1 className="va-title">Brands</h1>
          <p className="va-subtitle">Manage the manufacturers displayed on the public brands page. Changes are reflected automatically.</p>
        </div>
        <div className="va-header-actions"><button className="va-button" onClick={openCreate}><FiPlus /> Add brand</button></div>
      </header>

      <div className="va-stats">
        <div className="va-stat"><span className="va-stat__icon"><FiTag /></span><div><strong>{brands.length}</strong><span>Total brands</span></div></div>
        <div className="va-stat"><span className="va-stat__icon"><FiImage /></span><div><strong>{withImage}</strong><span>Uploaded logos</span></div></div>
        <div className="va-stat"><span className="va-stat__icon"><FiCheckCircle /></span><div><strong>{withDescription}</strong><span>Complete descriptions</span></div></div>
        <div className="va-stat"><span className="va-stat__icon"><FiSearch /></span><div><strong>{filteredBrands.length}</strong><span>Current results</span></div></div>
      </div>

      <div className="va-toolbar">
        <div className="va-search"><FiSearch /><input type="search" value={searchTerm} onChange={(event) => setSearchTerm(event.target.value)} placeholder="Search brand or description" aria-label="Search brands" /></div>
        <span className="va-results">{filteredBrands.length} brand{filteredBrands.length === 1 ? '' : 's'}</span>
      </div>

      {error && <div className="va-alert" role="alert"><FiAlertTriangle /> {error}</div>}
      {success && <div className="va-alert va-alert--success" role="status"><FiCheckCircle /> {success}</div>}

      <div className="va-table-wrap">
        <table className="va-table">
          <thead><tr><th>Brand</th><th>Description</th><th>Stored logo key</th><th></th></tr></thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="4"><div className="va-loading"><div className="va-spinner" aria-label="Loading brands" /></div></td></tr>
            ) : filteredBrands.length ? filteredBrands.map((brand) => (
              <tr key={brand.idbrand}>
                <td><div className="va-brand-cell"><BrandLogo brand={brand} /><div><span className="va-table__primary">{brand.brand_name}</span><span className="va-table__secondary">Brand ID #{brand.idbrand}</span></div></div></td>
                <td><span className="va-brand-description">{brand.brand_description || 'No description added.'}</span></td>
                <td><code className="va-code">{brand.brand_image || '—'}</code></td>
                <td><div className="va-table__actions">
                  <button className="va-button va-button--ghost va-button--icon" onClick={() => openEdit(brand)} title="Edit brand" aria-label={`Edit ${brand.brand_name}`}><FiEdit2 /></button>
                  <button className="va-button va-button--ghost va-button--icon" onClick={() => handleDelete(brand)} title="Delete brand" aria-label={`Delete ${brand.brand_name}`}><FiTrash2 /></button>
                </div></td>
              </tr>
            )) : (
              <tr><td colSpan="4"><div className="va-empty"><div><FiTag /><strong>No brands found</strong><p>Try a different search or add a new brand.</p></div></div></td></tr>
            )}
          </tbody>
        </table>
      </div>

      {formOpen && (
        <div className="va-modal-overlay" role="presentation" onMouseDown={closeForm}>
          <div className="va-modal va-modal--wide" role="dialog" aria-modal="true" aria-labelledby="brand-form-title" onMouseDown={(event) => event.stopPropagation()}>
            <div className="va-brand-form-header">
              <div><span className="va-eyebrow">{editingBrand ? 'Edit manufacturer' : 'New manufacturer'}</span><h3 id="brand-form-title">{editingBrand ? `Edit ${editingBrand.brand_name}` : 'Add a brand'}</h3><p>The public `/brands` page reads directly from this information.</p></div>
              <button className="va-button va-button--ghost va-button--icon" type="button" onClick={closeForm} aria-label="Close"><FiX /></button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="va-brand-form-preview"><BrandLogo brand={formData} size="large" /><div><strong>{formData.brand_name || 'Brand preview'}</strong><span>{formData.brand_description || 'The manufacturer description will appear here.'}</span></div></div>
              <div className="va-form-grid va-form-grid--single">
                <div className="va-field"><label htmlFor="brand-name">Brand name <span>Required</span></label><input id="brand-name" className="va-input" value={formData.brand_name} onChange={handleNameChange} placeholder="e.g. Phoenix Contact" required autoFocus /></div>
                <div className="va-field"><label htmlFor="brand-description">Description</label><textarea id="brand-description" className="va-textarea" value={formData.brand_description} onChange={(event) => setFormData({ ...formData, brand_description: event.target.value })} placeholder="Describe the manufacturer and its main product offering." /></div>
                <div className="va-field"><label htmlFor="brand-image-file">Logo image</label><input id="brand-image-file" className="va-input" type="file" accept="image/jpeg,image/png,image/webp" onChange={(event) => setBrandImageFile(event.target.files?.[0] || null)} /><small>{brandImageFile?.name || (editingBrand?.brand_image ? `Current image: ${editingBrand.brand_image}` : 'JPG, PNG or WEBP, maximum 5 MB. The file will be stored on Render.')}</small></div>
              </div>
              <div className="va-form-actions"><button type="button" className="va-button va-button--secondary" onClick={closeForm} disabled={saving}>Cancel</button><button type="submit" className="va-button" disabled={saving}>{editingBrand ? <FiEdit2 /> : <FiPlus />}{saving ? 'Saving...' : editingBrand ? 'Save changes' : 'Add brand'}</button></div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
